/** One preflight item hosted inside a wizard step.
 *
 *  The second host of `PreflightItemDetailContent` (after the Preflight tab /
 *  Items drawer) — same detail body, same `usePreflightItemActions` mutations
 *  and query keys, so the wizard and the tab can never disagree about an item.
 *  Adds the wizard's own concerns: the missing-input report, the blocked-by
 *  notice, and an inline TaskProgressCard when the item owns a background task
 *  (data-sync items record `context.bgtask`).
 */
import { Alert, Box, Chip, Paper, Typography } from '@mui/material';
import { useState } from 'react';

import type { PreflightItem, PreflightStatus } from '@/api/preflight';
import { BlockedNotice } from '@/features/preflight/PreflightBlockers';
import { PreflightItemActionBar } from '@/features/preflight/PreflightItemActionBar';
import { STATUS_LABELS, statusChipColor } from '@/features/preflight/status';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';

import { PreflightItemDetailContent } from '../tabs/PreflightItemDetailContent';
import { PreflightItemRunLogDrawer } from '../tabs/PreflightRunLog';
import type { PreflightItemActions } from '../tabs/usePreflightItemActions';

export function taskIdOf(item: PreflightItem): string | null {
  const bgtask = item.context?.bgtask as { id?: unknown } | undefined;
  return bgtask && typeof bgtask.id === 'string' && bgtask.id ? bgtask.id : null;
}

interface Props {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  actions: PreflightItemActions;
}

export function SetupItemPanel({ item, topId, toCode, canManage, actions }: Props) {
  const [logOpen, setLogOpen] = useState(false);
  const taskId = taskIdOf(item);
  // Inputs the field editor below renders on the field itself (matched by `key`);
  // only what has nowhere else to go is reported up here.
  const fieldKeys = new Set(item.config_fields.map((f) => f.key));
  const unanchored = item.missing_inputs.filter((m) => !fieldKeys.has(m.key));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={STATUS_LABELS[item.status]} color={statusChipColor(item.status)} />
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {item.title}
          {item.optional && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              (opt)
            </Typography>
          )}
        </Typography>
        <PreflightItemActionBar
          item={item}
          canManage={canManage}
          busy={actions.isBusy(item.id)}
          onPreview={() => actions.onPreview(item.id)}
          onRun={() => actions.onRun(item.id)}
          onExecute={() => actions.onExecute(item.id)}
          onComplete={() => actions.onComplete(item.id)}
          onViewLog={() => setLogOpen(true)}
        />
      </Box>

      {item.status !== 'completed' && item.blocked_by.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <BlockedNotice blockers={item.blocked_by} />
        </Alert>
      )}
      {item.status !== 'completed' && !item.ready && unanchored.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Needs input: {unanchored.map((m) => `${m.label} (${m.why})`).join('; ')}
        </Alert>
      )}

      {taskId && (
        <Box sx={{ mb: 1.5 }}>
          <TaskProgressCard taskId={taskId} onTerminal={() => actions.invalidate()} />
        </Box>
      )}

      <PreflightItemDetailContent
        item={item}
        topId={topId}
        toCode={toCode}
        canManage={canManage}
        pending={actions.isBusy(item.id)}
        onConfigure={(values) => actions.onConfigure(item.id, values)}
        onStepChange={(step, status?: PreflightStatus) => actions.onStepChange(item.id, step, status)}
        onChanged={actions.invalidate}
        lastSave={actions.lastConfigureRun(item.id)}
        onDismissLastSave={() => actions.dismissConfigureRun(item.id)}
      />

      <PreflightItemRunLogDrawer
        topId={topId}
        itemId={logOpen ? item.id : null}
        title={item.title}
        open={logOpen}
        onClose={() => setLogOpen(false)}
      />
      {/* The preview dialog is rendered ONCE by the step host — `actions` (and its
          previewRun) are shared across the step's panels. */}
    </Paper>
  );
}
